import { AccountState, AccountType } from '../../account/api/account-types';
import { TransactionStatus, TransactionType } from '../../transaction/api/transaction-types';
import { toDollars, toPennies } from '../../utils/format';
import { format, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { getDateInfo } from './date-info-cache';
/**
 * Compute the budget summary given accounts, transactions, and buckets.
 *
 * High-level overview:
 *
 * 1. **Initialize state**
 *    – Create maps/sets for months, accounts, buckets, credit-card metrics, and various balance trackers.
 *    – Include "off-budget" accounts in balance tracking but exclude them from budget/net-worth calculations.
 *
 * 2. **Determine date bounds**
 *    – Capture today's date in the user's timezone.
 *    – Prepare to record the earliest real (non-future) transaction date.
 *
 * 3. **Process each transaction**
 *    – **Skip** future-dated transactions (once we hit today, disable future checks).
 *    – **Off-budget** accounts: update only their balances and counts, then continue.
 *    – **On-budget** accounts:
 *        • Track uncategorized outflows in the current month.
 *        • Aggregate monthly inflows/outflows.
 *        • Update reconciled/cleared/uncleared balances and counts.
 *        • Record per-bucket net outflow.
 *        • For credit cards, separate charges, credits, and payments.
 *        • Update per-account monthly balance and running total.
 *
 * 4. **Build the full month list**
 *    – From the earliest transaction month through December of next year.
 *
 * 5. **Compute cumulative balances**
 *    – For each account, roll up monthly deltas into cumulative balances per month.
 *
 * 6. **Assemble per-month summaries**
 *    – For each month:
 *        • Populate account balances, total inflow/outflow.
 *        • Compute bucket assignments, outflows, and available funds.
 *        • Calculate credit-card payment pools, adjustments, and remaining debt.
 *        • Derive total budget, net worth, available funds, and assignment balance.
 *
 * 7. **Compile overall account summary**
 *    – Sum final balances by account type (assets, liabilities, credit cards, off-budget).
 *    – Convert all penny values into dollar amounts.
 *
 * 8. **Return**
 *    – A `BudgetSummary` containing per-month breakdowns, the initial transaction date, and the overall `AccountSummary`.
 *
 * @param timeZone     User's IANA timezone string
 * @param accounts     List of all account objects
 * @param transactions List of all transactions (sorted descending by dateEpoch)
 * @param buckets      List of budget bucket definitions
 * @returns            Fully populated `BudgetSummary`
 */
export const computeBudgetSummary = (timeZone, accounts, transactions, buckets) => {
    // const label = 'Computing budget summary';
    // console.log(label);
    // console.time(label);
    // Initialize earliest-date epoch and placeholder for final Date
    let earliestDateEpoch = toZonedTime(new Date(), timeZone).getTime();
    let earliestDate;
    const state = initializeBudgetState(accounts);
    // Get today's date in user's timezone
    const todayInTimeZone = toZonedTime(new Date(), timeZone);
    const today = startOfDay(todayInTimeZone);
    const todayEpoch = today.getTime();
    const currentYearMonth = format(today, 'yyyy-MM');
    // Optimization: Since transactions are sorted descending, once we find a transaction
    // that is not in the future, all following transactions will also not be in the future.
    let futureCheckRequired = true;
    // Process transactions
    for (let i = 0, len = transactions.length; i < len; ++i) {
        const transaction = transactions[i];
        const txAccount = state.accountMap.get(transaction.accountId);
        const { dateEpoch, inflow = 0, outflow = 0, bucketId, accountId, type, status } = transaction;
        // Use cached date info (with epoch fields) instead of recalculating
        const { zonedEpoch, dayStartEpoch, yearMonth } = getDateInfo(timeZone, dateEpoch);
        const outflowPennies = toPennies(outflow);
        const inflowPennies = toPennies(inflow);
        const amountPennies = inflowPennies - outflowPennies;
        // Handle off-budget accounts separately
        if (txAccount?.type === AccountType.OFF_BUDGET_ASSET || txAccount?.type === AccountType.OFF_BUDGET_LIABILITY) {
            if (futureCheckRequired) {
                if (dayStartEpoch > todayEpoch) {
                    continue;
                }
            }
            updateBalanceTracking(state, accountId, amountPennies, status);
            updateAccountBalances(state, accountId, yearMonth, amountPennies);
            continue;
        }
        // Only check warning for uncategorized in current month
        if (yearMonth === currentYearMonth && type === TransactionType.TRANSACTION && outflow !== 0 && !bucketId) {
            const currentUncategorized = state.uncategorizedBalanceById.get(accountId) || 0;
            state.uncategorizedBalanceById.set(accountId, currentUncategorized + outflowPennies);
        }
        // Future check for on-budget
        if (futureCheckRequired) {
            if (dayStartEpoch > todayEpoch) {
                continue;
            }
            else {
                futureCheckRequired = false;
            }
        }
        // Update earliestDateEpoch for each non-future transaction.
        earliestDateEpoch = zonedEpoch;
        // Aggregate inflows/outflows
        if (transaction.type === TransactionType.TRANSACTION) {
            const currIn = state.inflowsByMonth.get(yearMonth) || 0;
            state.inflowsByMonth.set(yearMonth, currIn + inflowPennies);
            const currOut = state.outflowsByMonth.get(yearMonth) || 0;
            state.outflowsByMonth.set(yearMonth, currOut + outflowPennies);
        }
        updateBalanceTracking(state, accountId, amountPennies, status);
        // Bucket outflow
        if (bucketId) {
            let byMonth = state.bucketOutflow.get(bucketId);
            if (!byMonth) {
                byMonth = new Map();
                state.bucketOutflow.set(bucketId, byMonth);
            }
            const co = byMonth.get(yearMonth) || 0;
            byMonth.set(yearMonth, co + outflowPennies - inflowPennies);
        }
        // Credit-card specifics
        if (txAccount?.type === AccountType.CREDIT_CARD) {
            processCreditCardTransaction(state, accountId, yearMonth, type, inflowPennies, outflowPennies);
        }
        updateAccountBalances(state, accountId, yearMonth, amountPennies);
    }
    // Reconstruct earliestDate from epoch
    earliestDate = new Date(earliestDateEpoch);
    const allMonths = generateMonthRange(earliestDate);
    // Precompute cumulative balances for each account over all months (including off-budget)
    const cumulativeBalancesByAccount = new Map();
    accounts.forEach((account) => {
        const monthlyChanges = state.accountBalancesByMonth.get(account.id) || new Map();
        let cumulative = 0;
        const cumulativeMap = new Map();
        allMonths.forEach((ym) => {
            cumulative += monthlyChanges.get(ym) || 0;
            cumulativeMap.set(ym, cumulative);
        });
        cumulativeBalancesByAccount.set(account.id, cumulativeMap);
    });
    const summaryByMonth = new Map();
    // Prepare perBucketAvailable
    const perBucketAvailable = new Map();
    // Process all months
    allMonths.forEach((yearMonth) => {
        const monthSummary = {
            bucketSummaryById: new Map(),
            creditCardPaymentsByAccount: new Map(),
            accountBalances: new Map(),
            totalBudget: 0,
            totalPaymentAdjustments: 0,
            totalAvailable: 0,
            totalCreditCardPaymentPool: 0,
            assignmentBalance: 0,
            netWorth: 0,
            totalInflow: toDollars(state.inflowsByMonth.get(yearMonth) || 0),
            totalOutflow: toDollars(state.outflowsByMonth.get(yearMonth) || 0),
        };
        const [yearStr, monthStr] = yearMonth.split('-');
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        // Calculate totalBudget and netWorth using cumulative balances (excluding off-budget accounts)
        let totalBudgetPennies = 0;
        let netWorthPennies = 0;
        accounts.forEach((account) => {
            const balance = cumulativeBalancesByAccount.get(account.id)?.get(yearMonth) || 0;
            monthSummary.accountBalances.set(account.id, toDollars(balance));
            // Only include on-budget accounts in totalBudget and netWorth calculations
            if (account.type !== AccountType.OFF_BUDGET_ASSET && account.type !== AccountType.OFF_BUDGET_LIABILITY) {
                if (account.type !== AccountType.CREDIT_CARD) {
                    totalBudgetPennies += balance;
                    netWorthPennies += balance;
                }
                else {
                    netWorthPennies += balance;
                }
            }
        });
        monthSummary.totalBudget = toDollars(totalBudgetPennies);
        monthSummary.netWorth = toDollars(netWorthPennies);
        // For each bucket
        buckets.forEach((bucket) => {
            const bucketSummary = processBucketForMonth(bucket, year, month, yearMonth, perBucketAvailable, state.bucketOutflow);
            monthSummary.bucketSummaryById.set(bucket.id, bucketSummary);
            monthSummary.totalAvailable += bucketSummary.available;
        });
        // Calculate credit card payments, totalPaymentAdjustments, and totalCreditCardPaymentPool
        accounts.forEach((account) => {
            if (account.type === AccountType.CREDIT_CARD) {
                const charges = state.creditCardCharges.get(account.id)?.get(yearMonth) || 0;
                const credits = state.creditCardCredits.get(account.id)?.get(yearMonth) || 0;
                const paymentMade = state.creditCardPayments.get(account.id)?.get(yearMonth) || 0;
                const assignment = account.assignments?.find((assignment) => assignment.year === year && assignment.month === month);
                const paymentAdjustment = toPennies(assignment?.amount || 0);
                monthSummary.totalPaymentAdjustments += toDollars(paymentAdjustment);
                const paymentDue = charges - credits;
                const leftover = state.creditCardLeftover.get(account.id) || 0;
                const paymentPool = account.state === AccountState.ARCHIVED ? 0 : paymentDue + paymentAdjustment + leftover - paymentMade;
                monthSummary.totalCreditCardPaymentPool += toDollars(paymentPool);
                state.creditCardLeftover.set(account.id, paymentPool);
                const balance = cumulativeBalancesByAccount.get(account.id)?.get(yearMonth) || 0;
                const remainingDebt = balance + paymentPool;
                monthSummary.creditCardPaymentsByAccount.set(account.id, {
                    charges: toDollars(charges),
                    credits: toDollars(credits),
                    paymentDue: toDollars(paymentDue),
                    paymentMade: toDollars(paymentMade),
                    paymentAdjustment: toDollars(paymentAdjustment),
                    leftover: toDollars(leftover),
                    paymentPool: toDollars(paymentPool),
                    balance: toDollars(balance),
                    remainingDebt: toDollars(remainingDebt),
                });
            }
        });
        const totalAvailablePennies = toPennies(monthSummary.totalAvailable);
        const totalCreditCardPaymentPoolPennies = toPennies(monthSummary.totalCreditCardPaymentPool);
        const assignmentBalancePennies = totalBudgetPennies - totalAvailablePennies - totalCreditCardPaymentPoolPennies;
        monthSummary.assignmentBalance = toDollars(assignmentBalancePennies);
        summaryByMonth.set(yearMonth, monthSummary);
    });
    const accountSummary = buildAccountSummary(accounts, state);
    const budgetSummary = {
        summaryByMonth,
        initialTransactionDate: format(earliestDate, 'yyyy-MM'),
        initialYear: earliestDate.getFullYear(),
        initialMonth: earliestDate.getMonth() + 1,
        accountSummary,
    };
    // console.timeEnd(label);
    return budgetSummary;
};
/**
 * Initialize all state tracking maps for budget computation.
 *
 * @param accounts Array of all accounts to track
 * @returns Initialized state object with all tracking maps
 */
function initializeBudgetState(accounts) {
    const state = {
        accountMap: new Map(),
        bucketOutflow: new Map(),
        creditCardCharges: new Map(),
        creditCardCredits: new Map(),
        creditCardPayments: new Map(),
        creditCardLeftover: new Map(),
        accountBalancesByMonth: new Map(),
        accountBalancesTotal: new Map(),
        uncategorizedBalanceById: new Map(),
        reconciledBalances: new Map(),
        clearedBalances: new Map(),
        unclearedBalances: new Map(),
        clearedBalanceCount: new Map(),
        unclearedBalanceCount: new Map(),
        inflowsByMonth: new Map(),
        outflowsByMonth: new Map(),
    };
    // Initialize account balances and tracking maps for ALL accounts (including off-budget)
    accounts.forEach((account) => {
        state.accountMap.set(account.id, account);
        state.accountBalancesByMonth.set(account.id, new Map());
        state.accountBalancesTotal.set(account.id, 0);
        state.reconciledBalances.set(account.id, 0);
        state.clearedBalances.set(account.id, 0);
        state.unclearedBalances.set(account.id, 0);
        state.clearedBalanceCount.set(account.id, 0);
        state.unclearedBalanceCount.set(account.id, 0);
        state.uncategorizedBalanceById.set(account.id, 0);
    });
    return state;
}
/**
 * Update balance tracking for a transaction based on its status.
 *
 * @param state Budget computation state
 * @param accountId Account ID for the transaction
 * @param amountPennies Transaction amount in pennies (positive for inflow, negative for outflow)
 * @param status Transaction status
 */
function updateBalanceTracking(state, accountId, amountPennies, status) {
    if (status === TransactionStatus.RECONCILED) {
        state.reconciledBalances.set(accountId, (state.reconciledBalances.get(accountId) || 0) + amountPennies);
    }
    if (status === TransactionStatus.CLEARED) {
        state.clearedBalanceCount.set(accountId, (state.clearedBalanceCount.get(accountId) || 0) + 1);
    }
    if (status === TransactionStatus.CLEARED || status === TransactionStatus.RECONCILED) {
        state.clearedBalances.set(accountId, (state.clearedBalances.get(accountId) || 0) + amountPennies);
    }
    if (status === TransactionStatus.PENDING) {
        state.unclearedBalances.set(accountId, (state.unclearedBalances.get(accountId) || 0) + amountPennies);
        state.unclearedBalanceCount.set(accountId, (state.unclearedBalanceCount.get(accountId) || 0) + 1);
    }
}
/**
 * Update account balances by month and total.
 *
 * @param state Budget computation state
 * @param accountId Account ID
 * @param yearMonth Year-month string (yyyy-MM)
 * @param amountPennies Amount to add in pennies
 */
function updateAccountBalances(state, accountId, yearMonth, amountPennies) {
    let accountYearMonthMap = state.accountBalancesByMonth.get(accountId);
    if (!accountYearMonthMap) {
        accountYearMonthMap = new Map();
        state.accountBalancesByMonth.set(accountId, accountYearMonthMap);
    }
    const currentMonthBalance = accountYearMonthMap.get(yearMonth) || 0;
    accountYearMonthMap.set(yearMonth, currentMonthBalance + amountPennies);
    const currentTotalBalance = state.accountBalancesTotal.get(accountId) || 0;
    state.accountBalancesTotal.set(accountId, currentTotalBalance + amountPennies);
}
/**
 * Process credit card specific updates for a transaction.
 *
 * @param state Budget computation state
 * @param accountId Account ID
 * @param yearMonth Year-month string (yyyy-MM)
 * @param type Transaction type
 * @param inflowPennies Inflow amount in pennies
 * @param outflowPennies Outflow amount in pennies
 */
function processCreditCardTransaction(state, accountId, yearMonth, type, inflowPennies, outflowPennies) {
    let chargesMap = state.creditCardCharges.get(accountId);
    let creditsMap = state.creditCardCredits.get(accountId);
    let paymentsMap = state.creditCardPayments.get(accountId);
    if (!chargesMap) {
        chargesMap = new Map();
        state.creditCardCharges.set(accountId, chargesMap);
    }
    if (!creditsMap) {
        creditsMap = new Map();
        state.creditCardCredits.set(accountId, creditsMap);
    }
    if (!paymentsMap) {
        paymentsMap = new Map();
        state.creditCardPayments.set(accountId, paymentsMap);
    }
    if (type === TransactionType.TRANSFER) {
        const currentPayments = paymentsMap.get(yearMonth) || 0;
        paymentsMap.set(yearMonth, currentPayments + inflowPennies);
    }
    else {
        const currentCharges = chargesMap.get(yearMonth) || 0;
        chargesMap.set(yearMonth, currentCharges + outflowPennies);
        const currentCredits = creditsMap.get(yearMonth) || 0;
        creditsMap.set(yearMonth, currentCredits + inflowPennies);
    }
}
/**
 * Generate a list of all months between the given date
 * and December of next calendar year, inclusive.
 *
 * @param earliestDate – a Date in the present or past
 * @returns an array of strings in “YYYY-MM” format,
 *   starting at earliestDate’s month and ending at next year’s December
 */
export function generateMonthRange(earliestDate) {
    const startYear = earliestDate.getFullYear();
    const startMonth = earliestDate.getMonth(); // 0–11
    const endYear = new Date().getFullYear() + 1;
    const months = [];
    for (let year = startYear; year <= endYear; year++) {
        const monthFrom = year === startYear ? startMonth : 0;
        const monthTo = 11;
        for (let m = monthFrom; m <= monthTo; m++) {
            const mm = String(m + 1).padStart(2, '0');
            months.push(`${year}-${mm}`);
        }
    }
    return months;
}
/**
 * Build account summary from final balances.
 *
 * @param accounts All accounts
 * @param state Budget computation state
 * @returns Account summary with categorized balances
 */
function buildAccountSummary(accounts, state) {
    const accountBalanceById = new Map();
    let creditCardBalancePennies = 0;
    let nonCreditCardBalancePennies = 0;
    let offBudgetAssetBalancePennies = 0;
    let offBudgetLiabilityBalancePennies = 0;
    accounts.forEach((account) => {
        if (account.state === AccountState.ACTIVE) {
            const balancePennies = state.accountBalancesTotal.get(account.id) || 0;
            accountBalanceById.set(account.id, balancePennies);
            // Categorize balances by account type
            if (account.type === AccountType.CREDIT_CARD) {
                creditCardBalancePennies += balancePennies;
            }
            else if (account.type === AccountType.OFF_BUDGET_ASSET) {
                offBudgetAssetBalancePennies += balancePennies;
            }
            else if (account.type === AccountType.OFF_BUDGET_LIABILITY) {
                offBudgetLiabilityBalancePennies += balancePennies;
            }
            else {
                nonCreditCardBalancePennies += balancePennies;
            }
        }
    });
    const liquidBudgetPennies = nonCreditCardBalancePennies;
    const totalAssetsPennies = nonCreditCardBalancePennies;
    const totalLiabilitiesPennies = -creditCardBalancePennies;
    const liquidNetWorthPennies = totalAssetsPennies - totalLiabilitiesPennies;
    const offBudgetNetWorthPennies = offBudgetAssetBalancePennies + offBudgetLiabilityBalancePennies;
    return {
        accountBalanceById: new Map(Array.from(accountBalanceById.entries()).map(([id, balance]) => [id, toDollars(balance)])),
        reconciledBalanceById: new Map(Array.from(state.reconciledBalances.entries()).map(([id, balance]) => [id, toDollars(balance)])),
        clearedBalanceById: new Map(Array.from(state.clearedBalances.entries()).map(([id, balance]) => [id, toDollars(balance)])),
        unclearedBalanceById: new Map(Array.from(state.unclearedBalances.entries()).map(([id, balance]) => [id, toDollars(balance)])),
        uncategorizedBalanceById: new Map(Array.from(state.uncategorizedBalanceById.entries()).map(([id, balance]) => [id, toDollars(balance)])),
        clearedBalanceCountById: new Map(state.clearedBalanceCount),
        unclearedBalanceCountById: new Map(state.unclearedBalanceCount),
        creditCardBalance: toDollars(creditCardBalancePennies),
        nonCreditCardBalance: toDollars(nonCreditCardBalancePennies),
        liquidBudget: toDollars(liquidBudgetPennies),
        liquidNetWorth: toDollars(liquidNetWorthPennies),
        offBudgetAssetBalance: toDollars(offBudgetAssetBalancePennies),
        offBudgetLiabilityBalance: toDollars(offBudgetLiabilityBalancePennies),
        offBudgetNetWorth: toDollars(offBudgetNetWorthPennies),
    };
}
/**
 * Process a single bucket's summary for a month.
 *
 * @param bucket Bucket to process
 * @param year Year to process
 * @param month Month to process (1-12)
 * @param yearMonth Year-month string (yyyy-MM)
 * @param perBucketAvailable Map tracking available amounts per bucket
 * @param bucketOutflow Map of bucket outflows by month
 * @returns Bucket summary for the month
 */
function processBucketForMonth(bucket, year, month, yearMonth, perBucketAvailable, bucketOutflow) {
    const bucketId = bucket.id;
    const leftoverPennies = Math.max(perBucketAvailable.get(bucketId) || 0, 0);
    const assignment = bucket.assignments.find((a) => a.year === year && a.month === month);
    const assignedAmount = assignment ? assignment.amount : 0;
    const assignedPennies = toPennies(assignedAmount);
    const bucketYearMonthMap = bucketOutflow.get(bucketId);
    const netOutflowPennies = bucketYearMonthMap ? bucketYearMonthMap.get(yearMonth) || 0 : 0;
    const availablePennies = leftoverPennies + assignedPennies - netOutflowPennies;
    perBucketAvailable.set(bucketId, availablePennies);
    return {
        leftover: toDollars(leftoverPennies),
        assigned: toDollars(assignedPennies),
        outflow: toDollars(netOutflowPennies),
        available: toDollars(availablePennies),
    };
}
